import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, SimpleChanges, forwardRef } from "@angular/core";
import { FormsModule, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { IonicModule } from "@ionic/angular";
import { TranslateModule } from "@ngx-translate/core";
import { format, parseISO } from 'date-fns';
import { DateFormatPipe } from "src/app/pipes/date-format/date-format";
import { Storage } from '@ionic/storage-angular';

/*
  _/﹋\_
  (҂`_´) - Exemplo de uso do componente ProverDatepicker:
  <;︻╦╤─ ҉ - - - - - - - - - - - - -

  <prover-datepicker
    [labelName]="'Nome da label'"         // Texto que aparecerá como label do campo
    [id]="'data'"                         // ID único do componente/modal
    [(dateTime)]="pessoa.dataNascimento"  // Valor do datepicker (two-way binding)
    formControlName="seuFormControl"      // Nome do FormControl para reactive forms
    [required]="true"                     // Indica se o campo é obrigatório
    [isJohrei]="true"                     // Apenas para o johrei
    [isColor]="{ 'color': '#ffff' }"           // Color em johrei
    [isDataAtual]="true"                  // Quando for data de nascimento etc.. usar true para limitar ate a data atual
    [isFuturo]="true"                     // Quando for true bloqueia datas retroativas podendo selecionar somente datas futuras 
    [disabled]="validations_form.get('dataHoraFim_data')?.disabled ?? false" // Para disabilitar a edição da data
  ></prover-datepicker>
*/
@Component({
  selector: 'prover-datepicker',
  templateUrl: 'prover-datepicker.html',
  styleUrls: ['prover-datepicker.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    DateFormatPipe,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProverDatepickerComponent),
      multi: true,
    },
  ]
})
export class ProverDatepickerComponent implements ControlValueAccessor {
  @Input() id: any;
  @Input() presentation: any;
  @Input() public labelName: any = 'Calendário';
  @Input() public required = false;
  @Input() public dateTime: string | null = null;
  @Output() public dateTimeChange = new EventEmitter<string>();
  @Input() public maxDate: string;
  @Input() public minDate: any;
  @Input() disabled: boolean = false;
  @Input() public isDataAtual = false;
  @Input() public isFuturo = false;
  @Input() public isJohrei = false;         
  @Input() public isColor: any;

  idioma: string = 'pt-BR';
  private innerValue: any;

  onChange: any = () => { };
  onTouched: any = () => { };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  constructor(
    private storage: Storage,
  ) {
    this.getIdioma();
  }

  ngOnInit(): void {
    this.updateMaxDate();
    this.updateMinDate();
    this.ensureDefaultDate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isDataAtual"]) {
      this.updateMaxDate();
    }

    if (changes["isFuturo"]) {
      this.updateMinDate();
    }
  }

  private updateMinDate() {
    const today = format(new Date(), "yyyy-MM-dd");
    this.minDate = this.isFuturo ? today : null;
  }

  private updateMaxDate() {
    const today = format(new Date(), "yyyy-MM-dd");
    const nextYear = new Date().getFullYear() + 1;

    this.maxDate = this.isDataAtual ? today : `${nextYear}-12-31`;
  }

  private ensureDefaultDate() {
    if (!this.dateTime) {
      const today = format(new Date(), "yyyy-MM-dd");
      this.dateTime = today;
      this.innerValue = today;
      this.dateTimeChange.emit(today);
      this.onChange(today);
    }
  }

  writeValue(value: any): void {
    this.innerValue = value;
    this.dateTime = value;
  }

  changeDate(event: any) {
    const dateTimeValue = format(parseISO(event.detail.value), "yyyy-MM-dd");
    this.dateTime = dateTimeValue;
    this.innerValue = dateTimeValue;

    this.dateTimeChange.emit(dateTimeValue);
    this.onChange(dateTimeValue);
    this.onTouched();
  }

  async getIdioma(): Promise<string | null> {
    const value = await this.storage.get('idioma');
    this.idioma = value || 'pt-BR';
    return value;
  }
}
